
import {
  computed,
  defineComponent, onMounted, onUnmounted, ref, Ref, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDropZone } from '@vueuse/core';
import RefreshPull from 'pull-refresh-vue3';
// eslint-disable-next-line import/no-cycle
import { useDriveStore } from '../store/driveStore';
import ItemMixed from '../components/items/ItemMixed.vue';
import AppDropZone from '@/components/AppDropZone.vue';
import {
  MEDIA_TYPES, MIXED_DATA_TYPE, NAVIGATION_PAGES, UPLOAD_PLACES,
} from '@/types/enums';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
// eslint-disable-next-line import/no-cycle
import MonitoringService from '@/services/MonitoringService';
import AppSkeletonFolder from '@/components/skeletons/AppSkeletonFolder.vue';
import DriveLoadBar from '@/modules/drive/components/UI/DriveLoadBar.vue';
import UsePagination from '@/composables/UsePagination';
import { DIRECTION } from '@/helpers/isScrollEnd';
import UsePullToRefresh from '@/composables/UsePullToRefresh';
import UseUpload from '@/composables/UseUpload';
import isPhoto from '@/helpers/fileDetector';
// eslint-disable-next-line import/no-cycle
import UploadService from '@/services/UploadService';

export default defineComponent({
  components: {
    ItemMixed,
    AppSkeletonFolder,
    RefreshPull,
    AppDropZone,
    DriveLoadBar,
  },
  setup() {
    const driveStore = useDriveStore();
    const mainStore = useMainStore();
    const route = useRoute();
    const router = useRouter();
    const { onUploadUpdate } = UseUpload();
    const backArrow = computed(() => (route.meta.backArrow));
    const isNetwork = computed(() => route.meta.isNetwork);

    const isDeleteConfirm: Ref<boolean> = ref(false);

    const {
      scroller, pageIdx, isPageLoading, onScroll, setLastPage,
    } = UsePagination();

    const loader: Ref<boolean> = ref(true);

    const hideSkeletons = computed(() => !!route.query.hideSkeletons);
    const currentPage = computed(() => route.meta.navigationPage);

    function fetchData() {
      return driveStore.getFolderById(pageIdx.value, route.params.id as string);
    }

    function handleScroll() {
      onScroll(fetchData, DIRECTION.BOTTOM, loader.value);
    }

    async function getFolder(noLoader?: boolean) {
      loader.value = !noLoader;
      // const sharedInfo = driveStore.currentFolder?.sharedLabels;

      return driveStore.getFolderById(pageIdx.value, route.params.id as string)
        .then(async (response) => {
          setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
        })
        .finally(() => {
          loader.value = false;
        });
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        pageIdx.value = 0;
        getFolder(true);
      }, 300);
    });

    const responseMessage = ref('');
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      if (newSearch.length === 0 || oldSearch.length === 0) {
        responseMessage.value = '';
      }
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        pageIdx.value = 0;
        fetchData().then((response) => {
          if (response.results.data.items.length === 0 && newSearch.length) {
            responseMessage.value = 'No search results found';
          }
          setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
        });
      }, 1000);
    });

    watch(() => route.params.id, () => {
      if (route.params.id && route.meta.navigationPage === NAVIGATION_PAGES.DRIVE_FOLDER) {
        pageIdx.value = 0;
        getFolder();
      }
    });

    watch(() => driveStore.isMixedSelector, (newVal, prevVal) => {
      if (prevVal && !newVal) {
        driveStore.currentFolder!.mixedData = driveStore.currentFolder!.mixedData!.map((item) => ({
          ...item,
          selected: false,
        }));
      }
    });

    function refreshFolder() {
      pageIdx.value = 0;

      return getFolder(true);
    }

    const {
      loadingPull, loosingText, successText, pullingText, loadingText, onRefresh,
    } = UsePullToRefresh(refreshFolder);

    function splitMedia(mediaList: File[]) {
      const fileList: File[] = [];
      const photoList: File[] = [];

      for (let i = 0; i < mediaList.length; i += 1) {
        if (isPhoto(mediaList[i])) {
          photoList.push(mediaList[i]);
        } else {
          fileList.push(mediaList[i]);
        }
      }

      return {
        fileList,
        photoList,
      };
    }

    function backAction(): void {
      router.back();
    }

    function onDrop(mediaList: File[] | null) {
      if (mediaList) {
        const { fileList, photoList } = splitMedia(mediaList);

        const fileUploads = UploadService.toFileUploads(fileList as unknown as FileList);
        const photoUploads = UploadService.toFileUploads(photoList as unknown as FileList);

        const folderId = currentPage.value === NAVIGATION_PAGES.DRIVE_FOLDER ? route.params.id as string : undefined;

        if (fileUploads.length) {
          UploadService.batchUploadTUS(fileUploads, onUploadUpdate, {
            location: folderId ? UPLOAD_PLACES.FOLDER : UPLOAD_PLACES.MIXED,
            id: folderId,
          }, folderId);
        }
        if (photoUploads.length) {
          UploadService.batchUploadTUS(photoUploads, onUploadUpdate, {
            location: folderId ? UPLOAD_PLACES.FOLDER_PHOTO : UPLOAD_PLACES.MIXED_PHOTO,
            id: folderId,
          }, folderId);
        }
      }
    }

    const { isOverDropZone } = useDropZone(scroller, onDrop);

    onMounted(() => {
      MonitoringService.sendPageMountedSpeed();
      driveStore.search = '';
      getFolder().then(() => {
        if (driveStore.currentFolder?.accountId.internalId !== mainStore.user.accountId
          && driveStore.currentFolder?.accountId.internalId !== mainStore.boundCorporate.internalId) {
          router.push(`/drive/network/folder/${driveStore.currentFolder!.id}`);
        }
      });
    });

    onUnmounted(() => {
      driveStore.resetCurrentFolder();
    });

    return {
      loader,
      mainStore,
      driveStore,
      scroller,
      hideSkeletons,
      isPageLoading,
      responseMessage,
      handleScroll,
      MEDIA_TYPES,
      MIXED_DATA_TYPE,

      loadingPull,
      loosingText,
      successText,
      pullingText,
      loadingText,
      onRefresh,
      isOverDropZone,
      backArrow,
      backAction,
    };
  },
});

